/**
 * product-gallery-vertical.js
 *
 * Enables up/down arrow navigation for the vertical thumbnail strip on the
 * product page (desktop ≥990px).  Scoped exclusively to the thumbnail slider
 * inside media-gallery elements that use a thumbnail-based desktop layout.
 *
 * What this does:
 *  - Intercepts Dawn's horizontal-scroll click handler (capture phase) so the
 *    prev/next buttons scroll the thumbnail list vertically instead.
 *  - Keeps the disabled state of both buttons accurate based on the actual
 *    vertical scroll position (Dawn's own update() always disables both buttons
 *    in vertical mode because its isSlideVisible() check is horizontal-only).
 *  - Fires on "media-gallery" custom-element definition so it runs after Dawn
 *    has already wired up its own event listeners.
 *
 * Mobile / tablet behaviour is completely unchanged (the MQL guard exits early
 * for viewports narrower than 990px, leaving Dawn's original handlers in place).
 */

(function () {
  var MQL = window.matchMedia('(min-width: 990px)');

  function initVerticalThumbnails() {
    document
      .querySelectorAll('media-gallery[data-desktop-layout*="thumbnail"]')
      .forEach(function (gallery) {
        var thumbComp = gallery.querySelector('.thumbnail-slider');
        if (!thumbComp) return;

        var sliderList = thumbComp.querySelector('[id^="Slider-Thumbnails"]');
        var prevBtn    = thumbComp.querySelector('.slider-button--prev');
        var nextBtn    = thumbComp.querySelector('.slider-button--next');
        if (!sliderList || !prevBtn || !nextBtn) return;

        /* ── helpers ──────────────────────────────────────────────── */

        function thumbScrollStep() {
          var first = sliderList.querySelector('.thumbnail-list__item');
          return first ? first.offsetHeight + 13 /* ~0.8rem gap */ : 90;
        }

        /*
         * Re-entrancy guard: prevents the MutationObserver from triggering
         * a recursive call to syncDisabledStates() when this function itself
         * modifies the disabled attribute.  Without the guard, each attribute
         * write fires the observer which calls this function again — creating
         * a tight loop that locks the main thread ("Page Unresponsive").
         *
         * Idempotent writes: we only call setAttribute / removeAttribute when
         * the desired state differs from the current state, so the observer
         * fires at most once per genuine state change rather than on every run.
         */
        var isSyncing = false;

        function syncDisabledStates() {
          if (!MQL.matches || isSyncing) return;
          isSyncing = true;

          var top       = sliderList.scrollTop;
          var maxScroll = sliderList.scrollHeight - sliderList.clientHeight;

          var wantPrevDisabled = top <= 1;
          var wantNextDisabled = maxScroll <= 0 || top >= maxScroll - 1;

          if (wantPrevDisabled && !prevBtn.hasAttribute('disabled')) {
            prevBtn.setAttribute('disabled', 'disabled');
          } else if (!wantPrevDisabled && prevBtn.hasAttribute('disabled')) {
            prevBtn.removeAttribute('disabled');
          }

          if (wantNextDisabled && !nextBtn.hasAttribute('disabled')) {
            nextBtn.setAttribute('disabled', 'disabled');
          } else if (!wantNextDisabled && nextBtn.hasAttribute('disabled')) {
            nextBtn.removeAttribute('disabled');
          }

          isSyncing = false;
        }

        /* ── button click override (capture phase, fires before Dawn) ─ */

        [prevBtn, nextBtn].forEach(function (btn) {
          btn.addEventListener(
            'click',
            function (e) {
              if (!MQL.matches) return; // leave Dawn's handler for mobile/tablet
              e.stopImmediatePropagation(); // prevent Dawn's horizontal scrollTo
              var step   = Number(btn.dataset.step) || 3;
              var amount = step * thumbScrollStep();
              sliderList.scrollBy({
                top: btn.name === 'next' ? amount : -amount,
                behavior: 'smooth',
              });
              // Sync after the smooth scroll settles
              setTimeout(syncDisabledStates, 350);
            },
            true /* capture — runs before Dawn's bubble-phase listener */
          );
        });

        /* ── keep states in sync on any scroll (wheel / touch / JS) ── */
        sliderList.addEventListener('scroll', syncDisabledStates);

        /* ── correct Dawn's initial update() which disables both buttons ─
              Dawn calls update() immediately after construction; we correct
              it once the layout has been painted.                          */
        requestAnimationFrame(function () {
          requestAnimationFrame(syncDisabledStates);
        });

        /* ── watch for Dawn re-applying disabled via update() on resize ─ */
        var attrObserver = new MutationObserver(function () {
          if (MQL.matches) syncDisabledStates();
        });
        attrObserver.observe(prevBtn, { attributes: true, attributeFilter: ['disabled'] });
        attrObserver.observe(nextBtn, { attributes: true, attributeFilter: ['disabled'] });
      });
  }

  /* Run after the media-gallery custom element is registered */
  if (customElements.get('media-gallery')) {
    initVerticalThumbnails();
  } else {
    customElements.whenDefined('media-gallery').then(initVerticalThumbnails);
  }
})();
